const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
})

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

const recentBookingDateFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  timeZone: 'UTC',
})

const timeFormatter24 = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

/** "2026-06-17T15:00:00" -> "17 June 2026" */
export const formatBookingDate = (iso: string) => dateFormatter.format(new Date(iso))

/** "2026-06-17T15:00:00" -> "3:00 pm" */
export const formatBookingTime = (iso: string) =>
  timeFormatter.format(new Date(iso)).toLowerCase()

/** "2026-06-17T15:00:00" -> "15:00" (24-hour format) */
export const formatBookingTime24 = (iso: string) => {
  if (!iso) return ''
  // If already HH:mm or H:mm format (with optional :ss)
  const timeOnlyMatch = /^\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*$/.exec(iso)
  if (timeOnlyMatch) {
    const [, h, m] = timeOnlyMatch
    return `${h.padStart(2, '0')}:${m}`
  }
  // If it has am/pm like "3:00 pm" or "03:00 PM"
  const ampmMatch = /^\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)\s*$/i.exec(iso)
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1], 10)
    const minutes = ampmMatch[2]
    const isPm = ampmMatch[3].toLowerCase() === 'pm'
    if (isPm && hours < 12) hours += 12
    if (!isPm && hours === 12) hours = 0
    return `${String(hours).padStart(2, '0')}:${minutes}`
  }
  const parsed = new Date(iso)
  if (!Number.isNaN(parsed.getTime())) {
    return timeFormatter24.format(parsed)
  }
  return iso
}

/** "2026-09-30" or ISO -> "Wed, 30 Sep", or fallback to formatted string */
export const formatRecentBookingDate = (dateStr: string) => {
  if (!dateStr) return ''

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr)
  if (match) {
    const [, year, month, day] = match
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
    if (
      date.getUTCFullYear() === Number(year) &&
      date.getUTCMonth() === Number(month) - 1 &&
      date.getUTCDate() === Number(day)
    ) {
      const parts = recentBookingDateFormatter.formatToParts(date)
      const weekday = parts.find((part) => part.type === 'weekday')?.value
      const dateDay = parts.find((part) => part.type === 'day')?.value
      const dateMonth = parts.find((part) => part.type === 'month')?.value
      if (weekday && dateDay && dateMonth) {
        return `${weekday}, ${dateDay} ${dateMonth}`
      }
    }
  }

  const parsed = new Date(dateStr)
  if (!Number.isNaN(parsed.getTime())) {
    const parts = recentBookingDateFormatter.formatToParts(parsed)
    const weekday = parts.find((part) => part.type === 'weekday')?.value
    const dateDay = parts.find((part) => part.type === 'day')?.value
    const dateMonth = parts.find((part) => part.type === 'month')?.value
    if (weekday && dateDay && dateMonth) {
      return `${weekday}, ${dateDay} ${dateMonth}`
    }
  }

  return dateStr
}

/** Converts passenger count to "X Passenger(s)" format */
export const formatPassengers = (count: string | number) => {
  const num = typeof count === 'string' ? parseInt(count, 10) : count
  return Number.isNaN(num) ? '' : `${num} ${num === 1 ? 'Passenger' : 'Passengers'}`
}

export const formatEuro = (amount: number) => `€${amount.toLocaleString('en-GB')}`
